import React, { useEffect, useMemo, useState } from 'react'
import { PrimaryButton, SecondaryButton } from '../../../components/Button'
import { PayoutType } from './payoutTypes'
import { ManagedPaymentMethod } from '../../../util/Managed'
import Radom, { Customer, ExternalAccount, IPriceQuote, PayoutFeeEstimate } from '../../../state/Radom'
import WarningBox from './WarningBox'
import { SectionBreakerHorizontal } from '../../../components/Shared'
import SummaryRow from './SummaryRow'
import DetailsRow from './DetailsRow'
import { Currency } from '../../../util/Currencies'
import CopyButton from '../../../components/CopyButton'

interface IPayoutReviewProps {
  payoutType: PayoutType
  testMode: boolean
  selectedFundingMethod: ManagedPaymentMethod | undefined
  selectedPayoutMethod: ManagedPaymentMethod | Currency | undefined
  selectedCustomer: Customer | null
  onContinue: () => void
  onBack: () => void
  payoutAmount: string
  networkName?: string
  isSubmitting?: boolean
  // External account for both fiat and crypto
  selectedExternalAccount?: ExternalAccount
  // Fee estimate
  feeEstimate: PayoutFeeEstimate | null
}

const PayoutReview = ({
  payoutType,
  testMode,
  selectedFundingMethod,
  selectedPayoutMethod,
  selectedCustomer,
  onContinue,
  onBack,
  payoutAmount,
  networkName,
  isSubmitting,
  selectedExternalAccount,
  feeEstimate
}: IPayoutReviewProps): JSX.Element => {
  const payoutCurrency = payoutType === PayoutType.Fiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker
    : selectedFundingMethod?.ticker || ''

  // Determine beneficiary display
  const beneficiaryName = selectedCustomer?.name || selectedCustomer?.companyName || ''
  const beneficiaryEmail = selectedCustomer?.email || ''

  const getDestinationDisplay = (): string => {
    if (!selectedExternalAccount) return '-'
    if (payoutType === PayoutType.Crypto) {
      const addr = selectedExternalAccount.crypto?.address || ''
      return addr.length > 12 ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : addr
    }
    // Fiat: use external account
    if (selectedExternalAccount.iban) {
      return `IBAN ****${selectedExternalAccount.iban.last4}`
    }
    if (selectedExternalAccount.account) {
      return `US ****${selectedExternalAccount.account.last4}`
    }
    return selectedExternalAccount.accountOwnerName
  }

  const formatFeeAmount = (value: number): string => {
    if (value === 0) return '0'
    return Number(value).toFixed(2)
  }

  const formatUsdcTotal = (value: number): string => {
    if (value === 0) return '0'
    return value.toFixed(6)
  }

  const isFiat = payoutType === PayoutType.Fiat
  const ticker = isFiat ? payoutCurrency : (selectedFundingMethod?.ticker || '')


  const quoteToCurrency = isFiat && selectedPayoutMethod && 'ticker' in selectedPayoutMethod
    ? selectedPayoutMethod.ticker.toUpperCase()
    : 'USD'

  const [priceQuote, setPriceQuote] = useState<IPriceQuote | null>(null)

  useEffect(() => {
    Radom.getPriceQuotes(['USDC'], [quoteToCurrency])
      .then(quotes => { if (quotes.length > 0) setPriceQuote(quotes[0]) })
      .catch(err => console.error('Failed to fetch price quote', err))
  }, [quoteToCurrency])

  const totalUsdcRequired = useMemo((): number | null => {
    if (!feeEstimate || !payoutAmount || Number(payoutAmount) <= 0) return null
    const fee = Number(feeEstimate.estimatedFeeUsd) || 0
    if (isFiat) {
      if (feeEstimate.estimatedTotalDebitUsd !== undefined && feeEstimate.estimatedTotalDebitUsd !== null) {
        return Number(feeEstimate.estimatedTotalDebitUsd)
      }

      if (!priceQuote) return null
      const rate = Number(priceQuote.value)
      if (!rate || isNaN(rate)) return null
      return Number(payoutAmount) / rate + fee
    }
    const networkFee = Number(feeEstimate.networkFeeUsd) || 0
    return Number(payoutAmount) + networkFee
  }, [feeEstimate, payoutAmount, isFiat, priceQuote])

  return (
    <div style={{
      maxWidth: 560,
      width: '100%',
      display: 'flex',
      padding: 28,
      flexDirection: 'column',
      borderRadius: 22,
      border: '1px solid #edf1f7',
      boxShadow: '0 10px 28px rgba(15, 23, 42, 0.035)',
      gap: 22,
      background: '#fff'
    }}>
      <h1 style={{ fontSize: 18.5, fontWeight: 500, lineHeight: '24px', margin: 0 }}>Confirm Payout</h1>
      <div style={{ display: 'flex', flexDirection: 'column', padding: 22, gap: 16, background: '#fbfcff', borderRadius: 18, border: '1px solid #edf1f7' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ color: '#747894', fontSize: 11.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em' }}>Recipient</span>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 16, fontWeight: 500, lineHeight: '24px' }}>{beneficiaryName || '-'}</span>
            <span style={{ color: '#747894', fontSize: 14, lineHeight: '20px', fontWeight: 400 }}>{beneficiaryEmail}</span>
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 400, lineHeight: '18px', display: 'flex', flexDirection: 'column', padding: 14, gap: 5, borderRadius: 14, background: 'white', border: '1px solid #edf1f7' }}>
            {payoutType === PayoutType.Crypto
              ? selectedExternalAccount?.crypto
                ? <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#20242d', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
                      {selectedExternalAccount.crypto.address}
                    </span>
                    <CopyButton text={selectedExternalAccount.crypto.address} />
                  </div>
                  <span style={{ color: '#747894' }}>Network: {selectedExternalAccount.crypto.network}</span>
                </>
                : <span>-</span>
              : selectedExternalAccount
                ? <>
                  <span>{selectedExternalAccount.accountOwnerName}</span>
                  <span style={{ color: '#747894' }}>{getDestinationDisplay()}</span>
                  {selectedExternalAccount.bankName && <span style={{ color: '#747894' }}>Bank: {selectedExternalAccount.bankName}</span>}
                  {selectedExternalAccount.iban?.bic && <span style={{ color: '#747894' }}>BIC: {selectedExternalAccount.iban.bic}</span>}
                </>
                : <span>-</span>
            }
          </div>
        </div>
        <SectionBreakerHorizontal/>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <DetailsRow title="Source" value={`${selectedFundingMethod?.ticker || 'USDC'} (${networkName || 'Ethereum'})`} />
          <DetailsRow title="Destination" value={payoutType === PayoutType.Crypto
            ? `${selectedFundingMethod?.ticker || ''} (${networkName || ''})`
            : `${payoutCurrency} (Bank Transfer)`
          } />
          <DetailsRow title="Amount" value={`${payoutAmount} ${ticker}`} />
          {isFiat && (
            <DetailsRow title="Payout Currency" value={payoutCurrency} />
          )}

          {/* Dynamic fee information */}
          <DetailsRow title="Estimated arrival" value="Instant" />
          {feeEstimate && (
            <>
              {isFiat
                ? (() => {
                  return <>
                    {feeEstimate.flatFeeUsd > 0 && (
                      <DetailsRow title="Flat Fee" value={`${formatFeeAmount(feeEstimate.flatFeeUsd)} USDC`} />
                    )}
                    {feeEstimate.minimumFeeUsd !== undefined && (
                      <DetailsRow title="Minimum Payout Fee" value={`${formatFeeAmount(feeEstimate.minimumFeeUsd)} USDC`} />
                    )}
                    <DetailsRow title="Payout Fee" value={`${formatFeeAmount(feeEstimate.estimatedFeeUsd)} USDC`} />
                    <DetailsRow title="Total deducted from balance" value={totalUsdcRequired !== null ? `${formatUsdcTotal(totalUsdcRequired)} USDC` : '-'} />
                  </>
                })()
                : (
                  <>
                    {feeEstimate.networkFeeUsd !== undefined && feeEstimate.networkFeeUsd !== null && (
                      <DetailsRow title="Network Fee" value={`${formatFeeAmount(feeEstimate.networkFeeUsd)} USDC`} />
                    )}
                  </>
                )
              }
            </>
          )}

          <SummaryRow
            title={payoutType === PayoutType.Crypto ? 'Total Sent' : 'Recipient Receives'}
            value={feeEstimate
              ? `${formatFeeAmount(feeEstimate.estimatedRecipientAmount)} ${ticker}`
              : `${payoutAmount} ${ticker}`
            }
          />
        </div>
      </div>

      <WarningBox payoutType={payoutType} />
      <div style={{ display: 'flex', width: '100%', gap: 12 }}>
        <SecondaryButton onClick={onBack} disabled={isSubmitting} style={{ width: '100%' }} >
            Back
        </SecondaryButton>

        <PrimaryButton
          onClick={onContinue}
          disabled={isSubmitting || !feeEstimate || totalUsdcRequired === null}
          style={{ width: '100%', opacity: isSubmitting ? 0.6 : 1 }} >
            {isSubmitting ? 'Submitting...' : 'Confirm & Send'}
        </PrimaryButton>
      </div>

    </div>

  )
}

export default PayoutReview
